<template>
  <div :data-testid="dataTestid" class="flex flex-col items-center gap-2">
    <div class="relative size-24" role="img" :aria-label="ariaLabel ?? `${caption}: ${label}`" :data-testid="`${dataTestid}-ring`">
      <VChart :option="option" :theme="OMR_ECHARTS_THEME" class="h-full w-full" :data-testid="`${dataTestid}-chart`" />
      <span
        class="absolute inset-0 flex items-center justify-center txt-headline-xl-bold text-main tabular-nums"
        :data-testid="`${dataTestid}-label`"
      >
        {{ label }}
      </span>
    </div>
    <div class="flex items-center gap-1">
      <p class="text-center txt-label-s-semibold text-subtle" :data-testid="`${dataTestid}-caption`" aria-hidden="true">{{ caption }}</p>
      <TooltipWrapper v-if="tooltip">
        <template #trigger>
          <button
            type="button"
            class="flex shrink-0 rounded-full text-icon-subtlest omr-focus-visible-outline hover:text-icon-main"
            :aria-label="t('Reviews.product.insights.metricCard.howToRead')"
            :aria-describedby="tooltipId"
            :data-testid="`${dataTestid}-tooltip-trigger`"
          >
            <CircleHelpIcon class="lucide-icon-s" aria-hidden="true" />
          </button>
        </template>
        <template #content>
          <MarkdownContent :id="tooltipId" unstyled class="max-w-sm" :md="tooltip" :data-testid="`${dataTestid}-tooltip`" />
        </template>
      </TooltipWrapper>
    </div>
  </div>
</template>

<script setup lang="ts">
import { MarkdownContent, TooltipWrapper } from '@ramp106/omrjs-core-ui'
import { CircleHelpIcon } from '@ramp106/omrjs-core-ui/icons'
import type { GaugeSeriesOption } from 'echarts/charts'
import { computed, useId } from 'vue'
import { useI18n } from 'vue-i18n'

import { OMR_ECHARTS_THEME } from '@/composables/useOmrEchartsTheme'
import { VChart, type OmrEChartsOption } from '@/config/echarts'

const RING_RADIUS = 48
const RING_WIDTH = 11
const RING_Z = 2
const MIN_VISIBLE_PROGRESS = 0.005

const FULL_TURN: GaugeSeriesOption = {
  type: 'gauge',
  startAngle: 90,
  endAngle: -270,
  min: 0,
  max: 1,
  silent: true,
  pointer: { show: false },
  axisTick: { show: false },
  splitLine: { show: false },
  axisLabel: { show: false },
  anchor: { show: false },
  title: { show: false },
  detail: { show: false },
}

const props = withDefaults(
  defineProps<{
    value: number | null
    label: string
    caption: string
    color: string
    trackColor: string
    baseline?: number | null
    deltaColor?: string
    ariaLabel?: string
    tooltip?: string
    dataTestid?: string
  }>(),
  { baseline: null, deltaColor: undefined, ariaLabel: undefined, tooltip: undefined, dataTestid: 'insights-progress-ring' },
)

const { t } = useI18n()
const tooltipId = useId()

const progressSeries = (value: number, color: string | undefined, z: number): GaugeSeriesOption => ({
  ...FULL_TURN,
  radius: RING_RADIUS,
  z,
  axisLine: { show: false },
  progress: { show: value >= MIN_VISIBLE_PROGRESS, width: RING_WIDTH, roundCap: true, itemStyle: { color } },
  data: [{ value }],
})

const ringSeries = (value: number, color: string | undefined): GaugeSeriesOption => ({
  ...progressSeries(value, color, RING_Z),
  axisLine: { lineStyle: { width: RING_WIDTH, color: [[1, props.trackColor]] } },
})

const option = computed<OmrEChartsOption>(() => {
  const value = props.value ?? 0
  if (props.baseline == null) return { series: [ringSeries(value, props.color)] }

  return {
    series: [
      ringSeries(Math.max(value, props.baseline), props.deltaColor),
      progressSeries(Math.min(value, props.baseline), props.color, RING_Z + 1),
    ],
  }
})
</script>
